import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTERFACE,
  REGISTRY,
  WORKER,
  SHAPES,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

const SourceChip: React.FC<{
  name: string;
  color: string;
  delay: number;
}> = ({ name, color, delay }) => {
  const frame = useCurrentFrame();
  const a = interpolate(frame, [delay, delay + 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        opacity: a,
        transform: `translateY(${(1 - a) * 16}px)`,
        fontFamily: FONTS.mono,
        fontSize: 30,
        fontWeight: 600,
        color: COLORS.text,
        background: COLORS.panel,
        border: `1.5px solid ${color}`,
        borderRadius: 10,
        padding: "14px 26px",
        boxShadow: `0 0 26px -14px ${color}`,
      }}
    >
      {name}
    </div>
  );
};

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="integration providers · the pattern" color={COLORS.cyan} />
        <Big>
          4 sources.{" "}
          <span style={{ color: COLORS.cyan }}>1</span> method call.
          <br />
          The pipeline never knows
          <br />
          which one it's talking to.
        </Big>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={HOOK} accent="cyan" />
  </AbsoluteFill>
);

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="without a contract" color={COLORS.red} />
        <div style={{ display: "flex", gap: 26, alignItems: "stretch" }}>
          <Panel accent="red" title="github" width={300}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 19, lineHeight: 1.9, color: COLORS.dim }}>
              <div>token auth</div>
              <div>git tree walk</div>
              <div>blob SHA dedup</div>
            </div>
          </Panel>
          <Panel accent="red" title="jira" width={300}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 19, lineHeight: 1.9, color: COLORS.dim }}>
              <div>basic auth</div>
              <div>JQL + startAt</div>
              <div>ADF JSON parse</div>
            </div>
          </Panel>
          <Panel accent="red" title="slack" width={300}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 19, lineHeight: 1.9, color: COLORS.dim }}>
              <div>bearer xoxb</div>
              <div>cursor paging</div>
              <div>mention resolve</div>
            </div>
          </Panel>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.red, marginTop: 30 }}>
          wire each into the pipeline → 4 pipelines
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="red" />
  </AbsoluteFill>
);

/* 03 — INTERFACE */
const IFACE = `// src/lib/integrations/types.ts
export interface IntegrationProvider {
  readonly providerType: string

  testConnection(
    settings: Record<string, unknown>
  ): Promise<ConnectionTestResult>

  fetchDocuments(
    settings: Record<string, unknown>,
    opts?: FetchProgressOptions
  ): Promise<SyncDocument[]>
}`;
export const Scene03Interface: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.interface.duration} />
    <AbsoluteFill style={{ ...center, paddingBottom: 30 }}>
      <Kicker text="the contract · types.ts" color={COLORS.cyan} />
      <CodePanel
        code={IFACE}
        language="tsx"
        filename="types.ts"
        highlightLines={[3, 11]}
        revealFrames={230}
        fontSize={24}
        width={1180}
      />
    </AbsoluteFill>
    <Caption lines={INTERFACE} accent="cyan" />
  </AbsoluteFill>
);

/* 04 — REGISTRY */
const REG = `// src/lib/integrations/index.ts
const providers: Record<string, IntegrationProvider> = {
  jira:       new JiraProvider(),
  confluence: new ConfluenceProvider(),
  slack:      new SlackProvider(),
  github:     new GitHubProvider(),
  // ...google_drive, youtube_channel, gmail
}

export function getProvider(type: string) {
  return providers[type] || null   // no switch, no if-else
}`;
export const Scene04Registry: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.registry.duration} />
    <AbsoluteFill style={{ ...center, paddingBottom: 30 }}>
      <Kicker text="string → singleton · index.ts" color={COLORS.green} />
      <CodePanel
        code={REG}
        language="tsx"
        filename="index.ts"
        highlightLines={[11]}
        revealFrames={210}
        fontSize={24}
        width={1180}
      />
    </AbsoluteFill>
    <Caption lines={REGISTRY} accent="green" />
  </AbsoluteFill>
);

/* 05 — WORKER (pipeline graph) */
const PIPE: PipeNode[] = [
  { label: "source row", value: "provider_type", sub: "from DB", accent: "cyan" },
  { label: "lookup", value: "getProvider()", sub: "registry", accent: "green" },
  { label: "fetch", value: "fetchDocuments", sub: "provider-blind", accent: "violet" },
  { label: "pipeline", value: "chunk · embed", sub: "SHA-dedup · store", accent: "amber" },
];
export const Scene05Worker: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.worker.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="sync-worker.ts · provider-agnostic" color={COLORS.green} />
      <div style={{ width: 1700 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={24} nodeWidth={230} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 40 }}>
        DOC_CONCURRENCY = 1 · in-process on the VPS via setTimeout(0)
      </div>
    </AbsoluteFill>
    <Caption lines={WORKER} accent="green" />
  </AbsoluteFill>
);

/* 06 — SHAPES → ONE DOCUMENT */
const chipColors = {
  github: COLORS.cyan,
  jira: COLORS.violet,
  confluence: COLORS.amber,
  slack: COLORS.magenta,
} as const;
export const Scene06Shapes: React.FC = () => {
  const frame = useCurrentFrame();
  const conv = interpolate(frame, [110, 150], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.shapes.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="normalize → one SyncDocument" color={COLORS.violet} />
        <div style={{ display: "flex", alignItems: "center", gap: 50 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <SourceChip name="github · repo files" color={chipColors.github} delay={8} />
            <SourceChip name="jira · ADF issues" color={chipColors.jira} delay={20} />
            <SourceChip name="confluence · XHTML" color={chipColors.confluence} delay={32} />
            <SourceChip name="slack · chat history" color={chipColors.slack} delay={44} />
          </div>
          <div
            style={{
              fontFamily: FONTS.display,
              fontSize: 70,
              color: COLORS.dim,
              opacity: conv,
            }}
          >
            →
          </div>
          <div style={{ opacity: conv }}>
            <Panel accent="green" title="SyncDocument" width={420}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.9, color: COLORS.text }}>
                <div>external_id</div>
                <div>title · content</div>
                <div>source_url</div>
                <div>
                  metadata.<span style={{ color: COLORS.green }}>content_type</span>
                </div>
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 34, opacity: conv }}>
          content_type ∈ {"{"} code · markdown · general {"}"} → chunking strategy
        </div>
      </AbsoluteFill>
      <Caption lines={SHAPES} accent="violet" />
    </AbsoluteFill>
  );
};

/* 07 — PAYOFF */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="cost to add a source" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 22 }}>
          <MetricCounter value={1} startFrame={10} durationFrames={45} accent="lime" suffix="" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 64, color: COLORS.text }}>
            new file
          </span>
          <span style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>+</span>
          <MetricCounter value={1} startFrame={28} durationFrames={45} accent="cyan" suffix="" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 64, color: COLORS.text }}>
            registry line
          </span>
        </div>
        <div style={{ opacity: c1, marginTop: 40 }}>
          <Callout label="proven · registry now holds 7" accent="green" width={760}>
            <b style={{ color: COLORS.text }}>github · jira · confluence · slack</b>{" "}
            + google_drive · youtube_channel · gmail — same interface, zero pipeline edits.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          One interface. One registry.
          <br />
          One provider-agnostic pipeline.
          <br />
          <span style={{ color: COLORS.cyan }}>The mess stays inside each provider.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · grounded retrieval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
